import { PageIcon } from "@/components/PageIcon";
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CalendarX, Plus, X, Check, Ban, Trash2, Loader2, ChevronLeft, ChevronRight } from "lucide-react";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fmt = (s) => (s ? new Date(s + "T00:00:00").toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "");
const WD = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const TYPE_COLOUR = {
  Sickness: "rose", "Unpaid leave": "slate", Compassionate: "purple", "Jury duty": "blue",
  Training: "indigo", Appointment: "amber", Holiday: "emerald", Other: "slate",
};
const chip = { emerald: "bg-emerald-100 text-emerald-700", amber: "bg-amber-100 text-amber-700", rose: "bg-rose-100 text-rose-700", slate: "bg-slate-100 text-slate-700", purple: "bg-purple-100 text-purple-700", blue: "bg-blue-100 text-blue-700", indigo: "bg-indigo-100 text-indigo-700" };
const sel = "w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 focus:outline-none";

const EMPTY = { user_id: "", type: "Sickness", start_date: "", end_date: "", half_day: false, paid: true, status: "pending", reason: "" };

export default function AbsencesPage() {
  const [data, setData] = useState({ items: [], can_manage: false, me: null, types: [], staff: [] });
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });

  const load = () => api.get("/hr/absences").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const days = useMemo(() => {
    const first = new Date(month.getFullYear(), month.getMonth(), 1);
    const startPad = (first.getDay() + 6) % 7;
    const total = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startPad; i++) cells.push(null);
    for (let d = 1; d <= total; d++) cells.push(new Date(month.getFullYear(), month.getMonth(), d));
    return cells;
  }, [month]);

  const absencesOn = (ds) => data.items.filter((a) => a.status !== "rejected" && ds >= a.start_date && ds <= a.end_date);

  const openDay = (ds) => {
    if (!data.can_manage) return;
    setF({ ...EMPTY, start_date: ds, end_date: ds });
    setAdding(true);
  };

  const submit = async () => {
    if (!f.user_id || !f.start_date || !f.end_date) { toast.error("Pick a staff member and dates"); return; }
    setBusy(true);
    try {
      const staff = data.staff.find((s) => s.id === f.user_id);
      await api.post("/hr/absences", { ...f, user_name: staff?.name });
      toast.success(f.status === "pending" ? "Absence logged — sent to Ross for approval" : "Absence logged");
      setF(EMPTY); setAdding(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };
  const decide = async (a, decision) => { try { await api.post(`/hr/absences/${a.id}/decision`, { decision }); toast.success(`Absence ${decision}`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const del = async (a) => { if (!window.confirm("Delete this absence record?")) return; try { await api.delete(`/hr/absences/${a.id}`); load(); } catch (e) { toast.error("Failed"); } };

  const pending = data.items.filter((a) => a.status === "pending");
  const monthLabel = month.toLocaleDateString("en-GB", { month: "long", year: "numeric" });

  return (
    <div className="px-6 lg:px-8 pt-3 lg:pt-4 pb-6 lg:pb-8" data-testid="absences-page">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-3"><PageIcon /> Absences</h1>
          <p className="text-sm text-slate-500 mt-1">Record and track staff absences — click a day to add one. Pending absences are sent to Ross for approval.</p>
        </div>
        {data.can_manage && <Button className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => { setF(EMPTY); setAdding(true); }} data-testid="absence-add-btn"><Plus className="w-4 h-4" /> Add absence</Button>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        {/* Calendar */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="absence-calendar">
          <div className="flex items-center justify-between mb-4">
            <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500" data-testid="absence-prev-month"><ChevronLeft className="w-4 h-4" /></button>
            <div className="font-bold text-slate-900">{monthLabel}</div>
            <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500" data-testid="absence-next-month"><ChevronRight className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-semibold text-slate-400 mb-1">{WD.map((d) => <div key={d}>{d}</div>)}</div>
          {loading ? <div className="py-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div> : (
            <div className="grid grid-cols-7 gap-1">
              {days.map((d, i) => {
                if (!d) return <div key={i} />;
                const ds = iso(d);
                const on = absencesOn(ds);
                return (
                  <button key={i} onClick={() => openDay(ds)} disabled={!data.can_manage}
                    className={`min-h-[64px] rounded-lg border border-slate-100 p-1 text-left align-top ${data.can_manage ? "hover:border-indigo-300 hover:bg-indigo-50/40 cursor-pointer" : "cursor-default"}`}
                    data-testid={`absence-day-${ds}`}>
                    <div className="text-[11px] text-slate-400">{d.getDate()}</div>
                    <div className="space-y-0.5 mt-0.5">
                      {on.slice(0, 3).map((a) => (
                        <div key={a.id} title={`${a.user_name} — ${a.type}${a.reason ? " — " + a.reason : ""}`}
                          className={`truncate text-[10px] px-1 py-0.5 rounded ${a.status === "approved" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"} ${a.user_id === data.me ? "ring-1 ring-indigo-400" : ""}`}>
                          {(a.user_name || "").split(" ")[0]} · {a.type}
                        </div>
                      ))}
                      {on.length > 3 && <div className="text-[10px] text-slate-400 px-1">+{on.length - 3} more</div>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          <div className="flex items-center gap-3 mt-3 text-[11px] text-slate-500">
            <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-emerald-100 border border-emerald-300" /> Approved</span>
            <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-amber-100 border border-amber-300" /> Pending</span>
            <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded ring-1 ring-indigo-400" /> You</span>
          </div>
        </div>

        {/* Pending approvals (Ross) */}
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="absence-approvals">
          <h2 className="text-base font-bold text-slate-900 mb-3">Awaiting Ross ({pending.length})</h2>
          {pending.length === 0 ? <div className="text-sm text-slate-400">Nothing awaiting approval.</div> : (
            <div className="space-y-2">
              {pending.map((a) => (
                <div key={a.id} className="rounded-xl border border-amber-200 bg-amber-50/50 p-3" data-testid={`absence-pending-${a.id}`}>
                  <div className="font-medium text-slate-900 text-sm">{a.user_name} <span className="text-xs font-normal text-slate-500">· {a.type}</span></div>
                  <div className="text-xs text-slate-500">{fmt(a.start_date)}{a.end_date !== a.start_date ? ` → ${fmt(a.end_date)}` : ""}</div>
                  {a.reason && <div className="text-xs text-slate-600 mt-1">“{a.reason}”</div>}
                  {data.can_manage && (
                    <div className="flex gap-2 mt-2">
                      <Button size="sm" className="h-7 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => decide(a, "approved")} data-testid={`absence-approve-${a.id}`}><Check className="w-3.5 h-3.5" /> Approve</Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-rose-600" onClick={() => decide(a, "rejected")} data-testid={`absence-reject-${a.id}`}><Ban className="w-3.5 h-3.5" /> Reject</Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Full records table */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        {loading ? <div className="p-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
          : data.items.length === 0 ? <div className="p-12 text-center text-sm text-slate-400">No absences recorded{data.can_manage ? " — click a day or Add absence to log one." : "."}</div>
          : (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 bg-slate-50/70 border-b border-slate-100">
                <th className="py-2.5 px-4">Staff</th><th className="py-2.5 px-2">Type</th>
                <th className="py-2.5 px-2">Dates</th><th className="py-2.5 px-2">Paid</th>
                <th className="py-2.5 px-2">Status</th><th className="py-2.5 px-4"></th>
              </tr></thead>
              <tbody className="divide-y divide-slate-50">
                {data.items.map((a) => (
                  <tr key={a.id} data-testid={`absence-row-${a.id}`}>
                    <td className="py-2.5 px-4 font-medium text-slate-800">{a.user_name}{a.reason && <div className="text-xs text-slate-400 font-normal truncate max-w-[220px]">{a.reason}</div>}</td>
                    <td className="py-2.5 px-2"><span className={`inline-flex px-2 py-0.5 rounded-md text-[11px] font-medium ${chip[TYPE_COLOUR[a.type] || "slate"]}`}>{a.type}</span></td>
                    <td className="py-2.5 px-2 text-slate-600">{fmt(a.start_date)}{a.end_date !== a.start_date ? ` → ${fmt(a.end_date)}` : ""}{a.half_day && <span className="text-xs text-slate-400"> · ½ day</span>}</td>
                    <td className="py-2.5 px-2">{a.paid ? <span className="text-emerald-600 text-xs font-medium">Paid</span> : <span className="text-slate-400 text-xs">Unpaid</span>}</td>
                    <td className="py-2.5 px-2"><span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${a.status === "approved" ? "bg-emerald-100 text-emerald-700" : a.status === "rejected" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>{a.status}</span></td>
                    <td className="py-2.5 px-4 text-right">
                      {data.can_manage && (
                        <div className="flex items-center gap-1 justify-end">
                          {a.status === "pending" && <>
                            <button onClick={() => decide(a, "approved")} className="text-emerald-600 hover:text-emerald-700" title="Approve" data-testid={`absence-approve-row-${a.id}`}><Check className="w-4 h-4" /></button>
                            <button onClick={() => decide(a, "rejected")} className="text-rose-500 hover:text-rose-600" title="Reject"><Ban className="w-4 h-4" /></button>
                          </>}
                          <button onClick={() => del(a)} className="text-slate-400 hover:text-rose-600" title="Delete" data-testid={`absence-delete-${a.id}`}><Trash2 className="w-4 h-4" /></button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      {adding && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex justify-end" onClick={() => setAdding(false)} data-testid="absence-form">
          <div className="bg-white h-full w-full sm:w-1/2 sm:max-w-none shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-300" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h3 className="text-xl font-bold text-slate-900">Add Absence</h3>
              <button onClick={() => setAdding(false)} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div><label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Staff member</label>
                <select className={`${sel} mt-1`} value={f.user_id} onChange={(e) => setF((s) => ({ ...s, user_id: e.target.value }))} data-testid="absence-staff">
                  <option value="">Select staff…</option>
                  {data.staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select></div>
              <div><label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Absence type</label>
                <select className={`${sel} mt-1`} value={f.type} onChange={(e) => setF((s) => ({ ...s, type: e.target.value }))} data-testid="absence-type">
                  {data.types.map((t) => <option key={t} value={t}>{t}</option>)}
                </select></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-semibold uppercase tracking-wide text-slate-500">From</label>
                  <Input type="date" value={f.start_date} onChange={(e) => setF((s) => ({ ...s, start_date: e.target.value }))} className="mt-1" data-testid="absence-start" /></div>
                <div><label className="text-xs font-semibold uppercase tracking-wide text-slate-500">To</label>
                  <Input type="date" value={f.end_date} onChange={(e) => setF((s) => ({ ...s, end_date: e.target.value }))} className="mt-1" data-testid="absence-end" /></div>
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer"><input type="checkbox" checked={f.half_day} onChange={(e) => setF((s) => ({ ...s, half_day: e.target.checked }))} data-testid="absence-halfday" /> Half day</label>
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer"><input type="checkbox" checked={f.paid} onChange={(e) => setF((s) => ({ ...s, paid: e.target.checked }))} data-testid="absence-paid" /> Paid</label>
              </div>
              <div><label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Status</label>
                <select className={`${sel} mt-1`} value={f.status} onChange={(e) => setF((s) => ({ ...s, status: e.target.value }))} data-testid="absence-status">
                  <option value="pending">Pending (send to Ross)</option><option value="approved">Approved</option><option value="rejected">Rejected</option>
                </select></div>
              <div><label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Reason / notes</label>
                <textarea value={f.reason} onChange={(e) => setF((s) => ({ ...s, reason: e.target.value }))} rows={3} className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100" data-testid="absence-reason" /></div>
            </div>
            <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setAdding(false)}>Cancel</Button>
              <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" disabled={busy} onClick={submit} data-testid="absence-submit">{busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Save absence</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
